import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { loadShipData } from "../store/features/shipBuilderSlice";
import { loadOptionalSystems } from "../store/features/optionalSystemsSlice";
import { loadBreakawayHull } from "../store/features/breakawayHullSlice";
import { saveShipDesign, loadShipDesign } from "../utils/saveLoad";
import { resetShip, setCurrentStep } from "../store/features/shipBuilderSlice";
import { resetOptionalSystems } from "../store/features/optionalSystemsSlice";
import { resetBreakawayHull } from "../store/features/breakawayHullSlice";
import { clearState } from "../utils/cache";

interface SaveLoadButtonsProps {
  className?: string;
  variant?: "compact" | "full";
  showPrint?: boolean;
  onPrint?: () => void;
}

export const SaveLoadButtons: React.FC<SaveLoadButtonsProps> = ({ 
  className = "", 
  variant = "full",
  showPrint = false,
  onPrint
}) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [customFilename, setCustomFilename] = useState("");

  // This component is for ship designs only (used in Ship Builder)
  const fileExtension = "MT2";
  const designType = "ship";

  // Handle escape key to close dialog
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showSaveDialog) {
        setShowSaveDialog(false);
        setCustomFilename("");
      }
    };

    if (showSaveDialog) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showSaveDialog]);

  const handleSave = () => {
    if (variant === "full") {
      setShowSaveDialog(true);
    } else {
      try {
        saveShipDesign(state);
      } catch (error) {
        console.error("Error saving ship design:", error);
        alert("Error saving ship design. Please try again.");
      }
    }
  };

  const handleSaveWithFilename = () => {
    try {
      const filename = customFilename.trim() ? `${customFilename.trim()}.MT2` : undefined;
      saveShipDesign(state, filename);
      setShowSaveDialog(false);
      setCustomFilename("");
    } catch (error) {
      console.error("Error saving ship design:", error);
      alert("Error saving ship design. Please try again.");
    }
  };

  const handleLoad = async () => {
    console.log("Load button clicked!");
    setIsLoading(true);
    try {
      console.log("Calling loadShipDesign()...");
      const loadedState = await loadShipDesign();
      console.log("loadShipDesign() returned:", loadedState);
      if (loadedState) {
        // This is the Ship Builder, so we only load ship data
        const hasShipData = loadedState.shipBuilder && (loadedState.shipBuilder.shipSize || loadedState.shipBuilder.hull || loadedState.shipBuilder.drives?.length > 0);
        
        console.log("Load Debug Info:", {
          hasShipData,
          loadedStateKeys: Object.keys(loadedState),
          shipBuilderKeys: loadedState.shipBuilder ? Object.keys(loadedState.shipBuilder) : null,
          shipSize: loadedState.shipBuilder?.shipSize,
          hull: loadedState.shipBuilder?.hull,
          drives: loadedState.shipBuilder?.drives
        });
        
        if (hasShipData) {
          console.log("Loading ship data in Ship Builder");
          // Load ship data
          if (loadedState.shipBuilder) {
            console.log("Dispatching loadShipData with:", loadedState.shipBuilder);
            dispatch(loadShipData(loadedState.shipBuilder));
          }
          if (loadedState.optionalSystems) {
            dispatch(loadOptionalSystems(loadedState.optionalSystems));
          }
          if (loadedState.breakawayHull) {
            dispatch(loadBreakawayHull(loadedState.breakawayHull));
          }
          console.log("Loaded ship data:", loadedState.shipBuilder);
        } else {
          console.log("No ship data found in loaded file");
          alert("No ship data found in the selected file. Please select a valid ship design file (.MT2).");
        }
      }
    } catch (error) {
      console.error("Error loading ship design:", error);
      console.error("Error details:", error);
      alert("Error loading ship design. Please try again.");
    } finally {
      console.log("Load process finished, setting isLoading to false");
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    console.log("Reset button clicked!");
    console.log("Current state:", state);
    
    const confirmMessage = "Are you sure? This will reset the ship design and clear all data.";
    
    if (window.confirm(confirmMessage)) {
      console.log("User confirmed reset");
      console.log("Resetting ship data");
      // Reset ship data
      dispatch(resetShip());
      dispatch(resetOptionalSystems());
      dispatch(resetBreakawayHull());
      dispatch(setCurrentStep(0));
      console.log("Clearing state cache");
      clearState();
      console.log("Reset completed");
    } else {
      console.log("User cancelled reset");
    }
  };

  if (variant === "compact") {
    return (
      <div className={`flex gap-2 ${className}`}>
        <button
          onClick={handleSave}
          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
          title={`Save ${designType} design as .${fileExtension} file`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          Save
        </button>
        <button
          onClick={handleLoad}
          disabled={isLoading}
          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          title={`Load ${designType} design from .${fileExtension} file`}
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          )}
          Load
        </button>
        <button
          onClick={handleReset}
          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors flex items-center gap-1"
          title={`Reset ${designType} design - Clear all data and start over`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset
        </button>
        {showPrint && onPrint && (
          <button
            onClick={onPrint}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-1"
            title="Print ship sheet"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
        <div className="flex-1">
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save {designType.charAt(0).toUpperCase() + designType.slice(1)} Design (.{fileExtension})
          </button>
          <p className="text-xs text-gray-600 mt-1 text-center">
            Save your current {designType} design to a file
          </p>
        </div>
        
        <div className="flex-1">
          <button
            onClick={handleLoad}
            disabled={isLoading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
            )}
            Load {designType.charAt(0).toUpperCase() + designType.slice(1)} Design (.{fileExtension})
          </button>
          <p className="text-xs text-gray-600 mt-1 text-center">
            Load a previously saved {designType} design
          </p>
        </div>

        <div className="flex-1">
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset {designType.charAt(0).toUpperCase() + designType.slice(1)} Design
          </button>
          <p className="text-xs text-gray-600 mt-1 text-center">
            Clear all data and start over
          </p>
        </div>

        {showPrint && onPrint && (
          <div className="flex-1">
            <button
              onClick={onPrint}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Ship Sheet
            </button>
            <p className="text-xs text-gray-600 mt-1 text-center">
              Print the current ship design
            </p>
          </div>
        )}
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Save {designType.charAt(0).toUpperCase() + designType.slice(1)} Design</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filename (optional)
              </label>
              <input
                type="text"
                value={customFilename}
                onChange={(e) => setCustomFilename(e.target.value)}
                placeholder="Enter custom filename"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use default: {(state.shipBuilder.finalDetails?.name || state.shipBuilder.shipDetails?.name || "ship")}_{new Date().toISOString().split('T')[0]}.{fileExtension}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setCustomFilename("");
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveWithFilename}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}; 